import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  ChangeEvent,
  CSSProperties,
  DragEvent as ReactDragEvent,
  PointerEvent as ReactPointerEvent,
} from 'react';
import { View, Text, StyleSheet, ActivityIndicator, useWindowDimensions } from 'react-native';
import { RotateCcw, RotateCw, Upload } from 'lucide-react-native';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { useTheme, SPACING, BORDER_RADIUS, FONT_SIZES, TYPOGRAPHY, withWeight } from '../../theme';
import { Button, ErrorBanner, Input } from '../common';
import { useUIStore } from '../../store/uiStore';
import { useAuthStore } from '../../store/authStore';
import { useServiceAreasStore } from '../../store/serviceAreasStore';
import { mapOverlaysApi, type CreateOverlayInput } from '../../api/mapOverlays';
import { fitCornersToBounds } from '../../utils/overlayCorners';
import { createPrefixedLogger } from '../../utils/logger';
import { DEFAULT_REGION } from './types';
import {
  defaultCropRect,
  defaultOverlayTitle,
  defaultSourceLabel,
  hiddenPdfPageCount,
  moveCropRect,
  resizeCropCorner,
  rotateBy,
  rotatedSize,
  scaleToLongSide,
  visiblePdfPageNumbers,
  EXPORT_JPEG_QUALITY,
  EXPORT_LONG_SIDE_CAP,
  IMAGE_RENDER_LONG_SIDE_CAP,
  NOTE_MAX_LENGTH,
  PDF_PAGE_RENDER_LONG_SIDE,
  SOURCE_LABEL_MAX_LENGTH,
  TITLE_MAX_LENGTH,
  type CropCorner,
  type CropRect,
  type Point,
  type RotationDeg,
  type Size,
} from '../../utils/planMapImport';

const log = createPrefixedLogger('PlanMapImportSheet');

// A moderator imports a page of a wildfire plan (PDF or image), rotates and
// crops away the legend/margins, names it, and this form uploads and
// creates the mapOverlays doc (always 'draft'). The placement editor
// (PlanMapPlacementPanel.web.tsx, a sibling built separately) opens next so
// they can drag it onto the map. Web only — canvas, a file input and
// pdfjs-dist have no native equivalent.
//
// The body half of the import surface (panel-system.md Phase 2, same split
// as DefensePlanAssignForm/Sheet): PlanMapImportSheet.web.tsx hosts this in
// an AdaptiveSheet on phone; MapLayersPanel.web.tsx pushes it into its own
// PanelStack on the desktop shell instead of opening a second dock. Either
// way this renders plain content (no ScrollView of its own) and owns no
// open/close state itself — `visible` drives the reset-on-(re)open effect
// below, and `onClose` is called once the import finishes or the moderator
// backs out, however the host presents that.

type Step = 'choose' | 'crop' | 'name';

const THUMB_WIDTH = 160;
const CROP_DISPLAY_MAX_WIDTH = 460;
const CROP_DISPLAY_MAX_HEIGHT = 420;
const CROP_HANDLE_SIZE = 14;

// pdfjs-dist is dynamic-imported only from inside handleFileChange's PDF
// branch (below), so it never enters the entry bundle — Metro code-splits at
// the import() boundary into its own lazy chunk. Cached so re-selecting a
// PDF in the same session doesn't re-fetch the chunk.
//
// The worker is served from our own origin (public/pdfjs/, copied from
// node_modules by `npm run pdfjs:worker` — see scripts/copy-pdfjs-worker.mjs)
// rather than pdfjs-dist's default CDN URL: the CSP has no `worker-src`, so a
// worker falls back to `script-src`, which only allows 'self' plus a short
// Google/Firebase allowlist — a pdfjs CDN host isn't on it, and a same-origin
// copy sidesteps loosening the CSP for one dependency.
//
// pdfjs-dist's own module has one `import.meta.url` reference (in
// NodeCanvasFactory, a Node.js-only code path this browser usage never
// reaches) and the worker's WASM decoders wrap their `import.meta.url` reads
// in try/catch already — unlike @react-pdf/renderer (see reportPdf.web.ts),
// nothing here needs the __ExpoImportMetaRegistry workaround.
let pdfjsPromise: Promise<typeof import('pdfjs-dist')> | null = null;
function getPdfjs(): Promise<typeof import('pdfjs-dist')> {
  if (!pdfjsPromise) {
    pdfjsPromise = import('pdfjs-dist').then((mod) => {
      mod.GlobalWorkerOptions.workerSrc = '/pdfjs/pdf.worker.min.mjs';
      return mod;
    });
  }
  return pdfjsPromise;
}

/** Decodes a File into something `drawImage` accepts, without a hard
 *  dependency on createImageBitmap (Safari's coverage of it is newer). */
async function loadImageSource(
  file: File
): Promise<CanvasImageSource & { width: number; height: number }> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file);
    } catch (err) {
      log.warn('createImageBitmap failed, falling back to <img>:', err);
    }
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not decode image'));
    };
    img.src = url;
  });
}

/** The service area the map opens on by default, used as the import's
 *  best-guess area. This sheet is mounted at the app root (alongside
 *  MapLayersSheet), not inside the map's own component tree, so it has no
 *  view into the live viewport at all. DEFAULT_REGION is Big Bear Lake —
 *  the area every fresh map load centers on — so this matches what the
 *  moderator is most likely looking at. The placement editor's corners get
 *  refit to the live view anyway; only `areaId` (which the placement editor
 *  never rewrites — see mapOverlaysApi.UpdateOverlayInput) rides on this
 *  guess, and the overlays subscription (useMapOverlaysLayer.web.ts) is
 *  global now, not keyed on it. */
function defaultImportArea(): {
  id: string;
  bounds: { minLat: number; maxLat: number; minLng: number; maxLng: number };
} {
  const found = useServiceAreasStore
    .getState()
    .findContaining(DEFAULT_REGION.latitude, DEFAULT_REGION.longitude);
  if (found) return { id: found.id, bounds: found.bounds };
  const pad = 0.05;
  return {
    id: 'unassigned',
    bounds: {
      minLat: DEFAULT_REGION.latitude - pad,
      maxLat: DEFAULT_REGION.latitude + pad,
      minLng: DEFAULT_REGION.longitude - pad,
      maxLng: DEFAULT_REGION.longitude + pad,
    },
  };
}

function renderStateForResize(size: Size, targetLongSide: number, cap: boolean): Size {
  return scaleToLongSide(size, targetLongSide, { allowUpscale: !cap });
}

export interface PlanMapImportFormProps {
  /** Whether this view is the one currently showing — drives the
   *  reset-on-open effect below. The AdaptiveSheet host keeps this mounted
   *  across opens (gorhom animates the close), so it needs the prop to know
   *  when to reset; the PanelStack push host mounts a fresh instance every
   *  time (PanelStack drops a popped view outright), so passing `true`
   *  there is equivalent to a fresh mount either way. */
  visible: boolean;
  onClose: () => void;
}

export function PlanMapImportForm({ visible, onClose }: PlanMapImportFormProps) {
  const { colors } = useTheme();
  const { width: windowWidth } = useWindowDimensions();
  const isModerator = useAuthStore((s) => s.isModerator);

  const [step, setStep] = useState<Step>('choose');
  const [fileName, setFileName] = useState('');
  const [numPages, setNumPages] = useState(0);
  const [selectedPage, setSelectedPage] = useState<number | null>(null);
  const [naturalSize, setNaturalSize] = useState<Size | null>(null);
  const [rotation, setRotation] = useState<RotationDeg>(0);
  const [cropRect, setCropRect] = useState<CropRect | null>(null);
  const [title, setTitle] = useState('');
  const [sourceLabel, setSourceLabel] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [busyMessage, setBusyMessage] = useState('');
  const [error, setError] = useState<string | null>(null);

  const pdfDocRef = useRef<PDFDocumentProxy | null>(null);
  const baseCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const dragRef = useRef<{ mode: 'move' | 'resize'; corner?: CropCorner; lastPoint: Point } | null>(
    null
  );
  // Drop-zone drag-over highlight only — the actual file comes from onDrop.
  const [dropActive, setDropActive] = useState(false);

  const resetAll = useCallback(() => {
    setStep('choose');
    setFileName('');
    setNumPages(0);
    setSelectedPage(null);
    setNaturalSize(null);
    setRotation(0);
    setCropRect(null);
    setTitle('');
    setSourceLabel('');
    setSourceUrl('');
    setNote('');
    setBusy(false);
    setBusyMessage('');
    setError(null);
    setDropActive(false);
    pdfDocRef.current = null;
    baseCanvasRef.current = null;
    dragRef.current = null;
  }, []);

  // Open-time reset only, same convention as AddPropertySheet.
  useEffect(() => {
    if (visible) resetAll();
  }, [visible, resetAll]);

  const bounds = naturalSize ? rotatedSize(naturalSize, rotation) : null;
  const availableWidth = Math.max(
    240,
    Math.min(CROP_DISPLAY_MAX_WIDTH, windowWidth - SPACING.lg * 2 - 8)
  );
  const displaySize = useMemo(() => {
    if (!bounds) return { width: availableWidth, height: CROP_DISPLAY_MAX_HEIGHT };
    const scale = Math.min(
      availableWidth / bounds.width,
      CROP_DISPLAY_MAX_HEIGHT / bounds.height,
      1
    );
    return {
      width: Math.max(1, Math.round(bounds.width * scale)),
      height: Math.max(1, Math.round(bounds.height * scale)),
    };
  }, [bounds, availableWidth]);

  // Redraw the rotated preview whenever the source or the rotation changes.
  useEffect(() => {
    const base = baseCanvasRef.current;
    const preview = previewCanvasRef.current;
    if (!base || !preview || !naturalSize || !bounds) return;
    preview.width = bounds.width;
    preview.height = bounds.height;
    const ctx = preview.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, bounds.width, bounds.height);
    ctx.save();
    ctx.translate(bounds.width / 2, bounds.height / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.drawImage(base, -naturalSize.width / 2, -naturalSize.height / 2);
    ctx.restore();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- bounds is derived from naturalSize+rotation
  }, [naturalSize, rotation]);

  // Reset to the full (rotated) canvas whenever the source or rotation
  // changes — simpler and less error-prone than transforming an in-progress
  // crop rect through a rotation, and a fresh full-image crop after
  // rotating is what a moderator expects anyway.
  useEffect(() => {
    if (!bounds) return;
    setCropRect(defaultCropRect(bounds));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- bounds is derived from naturalSize+rotation
  }, [naturalSize, rotation]);

  const pointFromEvent = useCallback(
    (e: { clientX: number; clientY: number }): Point | null => {
      const wrap = wrapRef.current;
      if (!wrap || !bounds || displaySize.width === 0 || displaySize.height === 0) return null;
      const rect = wrap.getBoundingClientRect();
      const scaleX = bounds.width / displaySize.width;
      const scaleY = bounds.height / displaySize.height;
      return {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY,
      };
    },
    [bounds, displaySize]
  );

  const handleMoveDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const p = pointFromEvent(e);
      if (!p) return;
      e.currentTarget.setPointerCapture(e.pointerId);
      dragRef.current = { mode: 'move', lastPoint: p };
    },
    [pointFromEvent]
  );

  const handleCornerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>, corner: CropCorner) => {
      e.stopPropagation();
      const p = pointFromEvent(e);
      if (!p) return;
      e.currentTarget.setPointerCapture(e.pointerId);
      dragRef.current = { mode: 'resize', corner, lastPoint: p };
    },
    [pointFromEvent]
  );

  const handlePointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current;
      if (!drag || !bounds) return;
      const p = pointFromEvent(e);
      if (!p) return;
      setCropRect((prev) => {
        if (!prev) return prev;
        if (drag.mode === 'resize' && drag.corner) {
          return resizeCropCorner(prev, drag.corner, p, bounds);
        }
        const dx = p.x - drag.lastPoint.x;
        const dy = p.y - drag.lastPoint.y;
        drag.lastPoint = p;
        return moveCropRect(prev, dx, dy, bounds);
      });
    },
    [bounds, pointFromEvent]
  );

  const handlePointerUp = useCallback(() => {
    dragRef.current = null;
  }, []);

  const loadPdf = useCallback(async (file: File) => {
    setBusy(true);
    setBusyMessage('Opening PDF…');
    setError(null);
    try {
      const pdfjs = await getPdfjs();
      const data = await file.arrayBuffer();
      const doc = await pdfjs.getDocument({ data }).promise;
      pdfDocRef.current = doc;
      setNumPages(doc.numPages);
    } catch (err) {
      log.warn('loadPdf failed:', err);
      setError("Couldn't open that PDF. Try a different file.");
    } finally {
      setBusy(false);
    }
  }, []);

  const loadImage = useCallback(async (file: File) => {
    setBusy(true);
    setBusyMessage('Loading image…');
    setError(null);
    try {
      const source = await loadImageSource(file);
      const target = renderStateForResize(
        { width: source.width, height: source.height },
        IMAGE_RENDER_LONG_SIDE_CAP,
        true
      );
      const base = document.createElement('canvas');
      base.width = target.width;
      base.height = target.height;
      const ctx = base.getContext('2d');
      if (!ctx) throw new Error('Canvas unavailable');
      ctx.drawImage(source, 0, 0, target.width, target.height);
      baseCanvasRef.current = base;
      setNaturalSize(target);
      setRotation(0);
      setTitle(defaultOverlayTitle(file.name));
      setSourceLabel(defaultSourceLabel(file.name));
      setStep('crop');
    } catch (err) {
      log.warn('loadImage failed:', err);
      setError("Couldn't load that image. Try a different file.");
    } finally {
      setBusy(false);
    }
  }, []);

  const processFile = useCallback(
    (file: File) => {
      setError(null);
      setFileName(file.name);
      setNumPages(0);
      setSelectedPage(null);
      setNaturalSize(null);
      pdfDocRef.current = null;
      baseCanvasRef.current = null;
      const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
      const isImage = file.type === 'image/jpeg' || file.type === 'image/png';
      if (isPdf) {
        void loadPdf(file);
      } else if (isImage) {
        void loadImage(file);
      } else {
        setError('Choose a PDF, JPG, or PNG file.');
      }
    },
    [loadPdf, loadImage]
  );

  const handleFileChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0] ?? null;
      // Clear so re-picking the same file later still fires onChange.
      e.target.value = '';
      if (!file) return;
      processFile(file);
    },
    [processFile]
  );

  const handleChooseFile = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleDragOver = useCallback((e: ReactDragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDropActive(true);
  }, []);

  const handleDragLeave = useCallback((e: ReactDragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDropActive(false);
  }, []);

  const handleDrop = useCallback(
    (e: ReactDragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDropActive(false);
      const file = e.dataTransfer.files?.[0] ?? null;
      if (file) processFile(file);
    },
    [processFile]
  );

  const handleSelectPage = useCallback(
    async (page: number) => {
      const doc = pdfDocRef.current;
      if (!doc) return;
      setBusy(true);
      setBusyMessage('Rendering page…');
      setError(null);
      try {
        const pdfPage = await doc.getPage(page);
        const viewport1 = pdfPage.getViewport({ scale: 1 });
        const target = renderStateForResize(
          { width: viewport1.width, height: viewport1.height },
          PDF_PAGE_RENDER_LONG_SIDE,
          false
        );
        const scale = target.width / viewport1.width;
        const viewport = pdfPage.getViewport({ scale });
        const base = document.createElement('canvas');
        base.width = Math.round(viewport.width);
        base.height = Math.round(viewport.height);
        const ctx = base.getContext('2d');
        if (!ctx) throw new Error('Canvas unavailable');
        await pdfPage.render({ canvas: base, viewport }).promise;
        baseCanvasRef.current = base;
        setSelectedPage(page);
        setNaturalSize({ width: base.width, height: base.height });
        setRotation(0);
        setTitle(defaultOverlayTitle(fileName, page));
        setSourceLabel(defaultSourceLabel(fileName, page));
        setStep('crop');
      } catch (err) {
        log.warn('handleSelectPage failed:', err);
        setError("Couldn't render that page. Try a different one.");
      } finally {
        setBusy(false);
      }
    },
    [fileName]
  );

  const renderPdfThumb = useCallback(async (page: number): Promise<string> => {
    const doc = pdfDocRef.current;
    if (!doc) throw new Error('PDF not loaded');
    const pdfPage = await doc.getPage(page);
    const viewport1 = pdfPage.getViewport({ scale: 1 });
    const scale = THUMB_WIDTH / viewport1.width;
    const viewport = pdfPage.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(viewport.width));
    canvas.height = Math.max(1, Math.round(viewport.height));
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas unavailable');
    await pdfPage.render({ canvas, viewport }).promise;
    return canvas.toDataURL('image/png');
  }, []);

  const handleImport = useCallback(async () => {
    if (!naturalSize || !cropRect || !bounds || !baseCanvasRef.current) return;
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setError('Give the map a title.');
      return;
    }
    const user = useAuthStore.getState().user;
    if (!user) {
      setError('Sign in again to import a map.');
      return;
    }
    setBusy(true);
    setBusyMessage('Preparing image…');
    setError(null);
    try {
      const rotated = document.createElement('canvas');
      rotated.width = bounds.width;
      rotated.height = bounds.height;
      const rctx = rotated.getContext('2d');
      if (!rctx) throw new Error('Canvas unavailable');
      rctx.translate(bounds.width / 2, bounds.height / 2);
      rctx.rotate((rotation * Math.PI) / 180);
      rctx.drawImage(baseCanvasRef.current, -naturalSize.width / 2, -naturalSize.height / 2);

      const outputSize = scaleToLongSide(
        { width: cropRect.width, height: cropRect.height },
        EXPORT_LONG_SIDE_CAP
      );
      const output = document.createElement('canvas');
      output.width = outputSize.width;
      output.height = outputSize.height;
      const octx = output.getContext('2d');
      if (!octx) throw new Error('Canvas unavailable');
      octx.drawImage(
        rotated,
        cropRect.x,
        cropRect.y,
        cropRect.width,
        cropRect.height,
        0,
        0,
        outputSize.width,
        outputSize.height
      );

      const blob = await new Promise<Blob>((resolve, reject) => {
        output.toBlob(
          (b) => (b ? resolve(b) : reject(new Error('Could not export the image'))),
          'image/jpeg',
          EXPORT_JPEG_QUALITY
        );
      });

      setBusyMessage('Uploading…');
      const id = mapOverlaysApi.newOverlayId();
      const { imagePath, imageUrl } = await mapOverlaysApi.uploadOverlayImage(id, blob);

      setBusyMessage('Saving…');
      const area = defaultImportArea();
      const corners = fitCornersToBounds(
        {
          north: area.bounds.maxLat,
          south: area.bounds.minLat,
          east: area.bounds.maxLng,
          west: area.bounds.minLng,
        },
        outputSize.width,
        outputSize.height
      );
      const source: CreateOverlayInput['source'] = {
        document: fileName,
        label: sourceLabel.trim() || fileName,
        ...(selectedPage != null ? { page: selectedPage } : {}),
        ...(sourceUrl.trim() ? { url: sourceUrl.trim() } : {}),
      };

      await mapOverlaysApi.createOverlay(id, {
        title: trimmedTitle,
        source,
        areaId: area.id,
        imagePath,
        imageUrl,
        imageWidth: outputSize.width,
        imageHeight: outputSize.height,
        corners,
        note: note.trim() || undefined,
        createdBy: user.id,
      });

      useUIStore.getState().setOverlayEnabled(id, true);
      onClose();
      useUIStore.getState().startOverlayPlacement(id, { fitToView: true });
    } catch (err) {
      log.warn('handleImport failed:', err);
      setError("Couldn't import the map. Try again.");
    } finally {
      setBusy(false);
    }
  }, [
    naturalSize,
    cropRect,
    bounds,
    rotation,
    title,
    sourceLabel,
    sourceUrl,
    note,
    fileName,
    selectedPage,
    onClose,
  ]);

  const visiblePages = useMemo(() => visiblePdfPageNumbers(numPages), [numPages]);
  const hiddenPages = useMemo(() => hiddenPdfPageCount(numPages), [numPages]);

  const cropBoxStyle: CSSProperties | undefined =
    cropRect && bounds
      ? {
          position: 'absolute',
          left: (cropRect.x / bounds.width) * displaySize.width,
          top: (cropRect.y / bounds.height) * displaySize.height,
          width: (cropRect.width / bounds.width) * displaySize.width,
          height: (cropRect.height / bounds.height) * displaySize.height,
          border: `2px solid ${colors.action}`,
          boxSizing: 'border-box',
          boxShadow: `0 0 0 9999px ${colors.overlay}`,
          cursor: 'move',
          touchAction: 'none',
        }
      : undefined;

  const CORNERS: CropCorner[] = ['topLeft', 'topRight', 'bottomLeft', 'bottomRight'];

  function handleStyle(corner: CropCorner): CSSProperties {
    const isLeft = corner === 'topLeft' || corner === 'bottomLeft';
    const isTop = corner === 'topLeft' || corner === 'topRight';
    return {
      position: 'absolute',
      width: CROP_HANDLE_SIZE,
      height: CROP_HANDLE_SIZE,
      left: isLeft ? -CROP_HANDLE_SIZE / 2 : undefined,
      right: !isLeft ? -CROP_HANDLE_SIZE / 2 : undefined,
      top: isTop ? -CROP_HANDLE_SIZE / 2 : undefined,
      bottom: !isTop ? -CROP_HANDLE_SIZE / 2 : undefined,
      borderRadius: 3,
      background: colors.action,
      border: `2px solid ${colors.actionOn}`,
      cursor: corner === 'topLeft' || corner === 'bottomRight' ? 'nwse-resize' : 'nesw-resize',
      touchAction: 'none',
    };
  }

  const canContinueFromCrop = cropRect != null;
  const canImport = title.trim().length > 0 && !busy;

  // Every hook above must run unconditionally on every render (Rules of
  // Hooks) — this early return has to come after all of them, not before.
  if (!isModerator) return null;

  return (
    <View testID="plan-map-import-sheet" style={styles.content}>
      <Text style={[styles.title, { color: colors.text }]}>Import a plan map</Text>
      <Text style={[styles.blurb, { color: colors.textSecondary }]}>
        {step === 'choose' && 'Choose a PDF page or an image of the map you want to add.'}
        {step === 'crop' && 'Rotate and crop away legends or margins, then continue.'}
        {step === 'name' && 'Name it so people can tell it apart from other plan maps.'}
      </Text>

      {step === 'choose' && (
        <View>
          <div
            data-testid="plan-map-import-dropzone"
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            style={{
              ...dropZoneStyle,
              borderColor: dropActive ? colors.action : colors.border,
              background: dropActive ? colors.hoverSurface : colors.surface,
            }}>
            <Upload size={28} color={colors.textSecondary} />
            <Text style={[styles.dropzoneText, { color: colors.text }]}>
              Drop a PDF or an image here
            </Text>
            <Button
              title="Choose a file"
              variant="secondary"
              size="sm"
              disabled={busy}
              onPress={handleChooseFile}
              style={styles.chooseButton}
              testID="plan-map-import-choose"
            />
            {fileName && (
              <Text
                style={[styles.fileNameText, { color: colors.textSecondary }]}
                numberOfLines={1}>
                {fileName}
              </Text>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,image/jpeg,image/png"
              data-testid="plan-map-import-file"
              onChange={handleFileChange}
              disabled={busy}
              style={hiddenFileInputStyle}
            />
          </div>
          {busy && step === 'choose' && (
            <View style={styles.busyRow}>
              <ActivityIndicator size="small" color={colors.text} />
              <Text style={[styles.busyText, { color: colors.textSecondary }]}>{busyMessage}</Text>
            </View>
          )}
          {numPages > 0 && (
            <View style={styles.pdfSection}>
              <Text style={[styles.sectionLabel, { color: colors.textSecondary }]}>
                Choose a page
              </Text>
              <View style={styles.thumbGrid}>
                {visiblePages.map((page) => (
                  <PdfPageThumb
                    key={page}
                    page={page}
                    selected={selectedPage === page}
                    disabled={busy}
                    onSelect={handleSelectPage}
                    renderThumb={renderPdfThumb}
                  />
                ))}
              </View>
              {hiddenPages > 0 && (
                <Text style={[styles.hiddenNote, { color: colors.textTertiary }]}>
                  {hiddenPages} more {hiddenPages === 1 ? 'page' : 'pages'} not shown.
                </Text>
              )}
            </View>
          )}
        </View>
      )}

      {step === 'crop' && naturalSize && bounds && (
        <View style={styles.cropStep}>
          <View style={styles.rotateRow}>
            <Button
              title="Rotate left"
              leftIcon={RotateCcw}
              variant="secondary"
              size="sm"
              disabled={busy}
              onPress={() => setRotation((r) => rotateBy(r, -90))}
              testID="plan-map-import-rotate-left"
            />
            <Button
              title="Rotate right"
              leftIcon={RotateCw}
              variant="secondary"
              size="sm"
              disabled={busy}
              onPress={() => setRotation((r) => rotateBy(r, 90))}
              testID="plan-map-import-rotate-right"
            />
          </View>

          <div
            ref={wrapRef}
            style={{
              position: 'relative',
              width: displaySize.width,
              height: displaySize.height,
              margin: '0 auto',
              touchAction: 'none',
            }}>
            <canvas
              ref={previewCanvasRef}
              style={{ width: displaySize.width, height: displaySize.height, display: 'block' }}
            />
            {cropBoxStyle && (
              <div
                data-testid="plan-map-import-crop"
                style={cropBoxStyle}
                onPointerDown={handleMoveDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}>
                {CORNERS.map((corner) => (
                  <div
                    key={corner}
                    data-testid={`plan-map-import-crop-handle-${corner}`}
                    style={handleStyle(corner)}
                    onPointerDown={(e) => handleCornerDown(e, corner)}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                    onPointerCancel={handlePointerUp}
                  />
                ))}
              </div>
            )}
          </div>
        </View>
      )}

      {step === 'name' && (
        <View>
          <Input
            label="Title"
            value={title}
            onChangeText={(text) => setTitle(text.slice(0, TITLE_MAX_LENGTH))}
            placeholder="Big Bear CWPP, p. 4"
            maxLength={TITLE_MAX_LENGTH}
            hint={`${title.length}/${TITLE_MAX_LENGTH}`}
            testID="plan-map-import-title"
          />
          <Input
            label="Source"
            value={sourceLabel}
            onChangeText={(text) => setSourceLabel(text.slice(0, SOURCE_LABEL_MAX_LENGTH))}
            placeholder="Where this map comes from"
            maxLength={SOURCE_LABEL_MAX_LENGTH}
            testID="plan-map-import-source-label"
          />
          <Input
            label="Source link (optional)"
            value={sourceUrl}
            onChangeText={setSourceUrl}
            placeholder="https://…"
            autoCapitalize="none"
            autoCorrect={false}
            keyboardType="url"
            testID="plan-map-import-source-url"
          />
          <Input
            label="Note (optional)"
            value={note}
            onChangeText={(text) => setNote(text.slice(0, NOTE_MAX_LENGTH))}
            placeholder="Anything readers should know"
            multiline
            numberOfLines={3}
            maxLength={NOTE_MAX_LENGTH}
            hint={`${note.length}/${NOTE_MAX_LENGTH}`}
            style={styles.noteInput}
            testID="plan-map-import-note"
          />
        </View>
      )}

      <ErrorBanner message={error} />

      <View style={styles.actions}>
        {step !== 'choose' ? (
          <Button
            title="Back"
            variant="ghost"
            size="sm"
            disabled={busy}
            onPress={() => setStep(step === 'name' ? 'crop' : 'choose')}
            testID="plan-map-import-back"
          />
        ) : (
          <View />
        )}

        {step === 'crop' && (
          <Button
            title="Continue"
            variant="primary"
            size="sm"
            disabled={!canContinueFromCrop || busy}
            onPress={() => setStep('name')}
            testID="plan-map-import-continue"
          />
        )}
        {step === 'name' && (
          <Button
            title={busy ? busyMessage || 'Importing…' : 'Import'}
            variant="primary"
            size="sm"
            loading={busy}
            disabled={!canImport}
            onPress={() => void handleImport()}
            testID="plan-map-import-submit"
          />
        )}
      </View>

      {/* Scroll sentinel: Maestro flows assert this is reachable. */}
      <View testID="plan-map-import-end" style={styles.scrollSentinel} />
    </View>
  );
}

function PdfPageThumb({
  page,
  selected,
  disabled,
  onSelect,
  renderThumb,
}: {
  page: number;
  selected: boolean;
  disabled: boolean;
  onSelect: (page: number) => void;
  renderThumb: (page: number) => Promise<string>;
}) {
  const { colors } = useTheme();
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const elRef = useRef<HTMLDivElement | null>(null);

  // Lazily renders once the thumbnail scrolls near the viewport, rather than
  // rasterizing up to 40 pages the moment the PDF opens.
  useEffect(() => {
    const el = elRef.current;
    if (!el || src) return;
    let cancelled = false;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        renderThumb(page)
          .then((url) => {
            if (!cancelled) setSrc(url);
          })
          .catch((err) => {
            log.warn('renderThumb failed:', err);
            if (!cancelled) setFailed(true);
          });
      },
      { rootMargin: '300px' }
    );
    observer.observe(el);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [page, renderThumb, src]);

  return (
    <div
      ref={elRef}
      data-testid={`plan-map-import-page-${page}`}
      role="button"
      aria-pressed={selected}
      aria-label={`Page ${page}`}
      onClick={() => !disabled && onSelect(page)}
      style={{
        width: THUMB_WIDTH,
        cursor: disabled ? 'not-allowed' : 'pointer',
        border: `2px solid ${selected ? colors.action : colors.border}`,
        borderRadius: BORDER_RADIUS.sm,
        overflow: 'hidden',
        opacity: disabled ? 0.6 : 1,
      }}>
      <div
        style={{
          minHeight: 100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: colors.surface,
        }}>
        {src ? (
          <img src={src} alt={`Page ${page}`} style={{ width: '100%', display: 'block' }} />
        ) : failed ? (
          <Text style={{ color: colors.textTertiary, fontSize: FONT_SIZES.meta }}>
            Couldn&apos;t render
          </Text>
        ) : (
          <ActivityIndicator size="small" color={colors.textTertiary} />
        )}
      </div>
      <Text
        style={[styles.thumbCaption, { color: selected ? colors.action : colors.textSecondary }]}>
        Page {page}
      </Text>
    </div>
  );
}

const dropZoneStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  padding: 32,
  borderRadius: BORDER_RADIUS.md,
  borderWidth: 2,
  borderStyle: 'dashed',
  textAlign: 'center',
};

// The real file input stays in the DOM (its accept list and testID are
// what Maestro/audits drive) but is visually hidden — the drop zone above
// is the surface a moderator actually sees; "Choose a file" opens this via
// a ref click.
const hiddenFileInputStyle: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

const styles = StyleSheet.create({
  content: {
    padding: SPACING.lg,
    paddingBottom: SPACING.xl,
  },
  title: {
    ...TYPOGRAPHY.h4,
    marginBottom: SPACING.xs,
  },
  blurb: {
    ...TYPOGRAPHY.bodySmall,
    marginBottom: SPACING.md,
  },
  busyRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
    marginTop: SPACING.xs,
  },
  busyText: {
    ...TYPOGRAPHY.bodySmall,
  },
  dropzoneText: {
    ...withWeight(TYPOGRAPHY.body, 'medium'),
  },
  chooseButton: {
    marginTop: SPACING.xxs,
  },
  fileNameText: {
    ...TYPOGRAPHY.caption,
    marginTop: SPACING.xxs,
  },
  pdfSection: {
    marginTop: SPACING.md,
  },
  sectionLabel: {
    ...TYPOGRAPHY.overline,
    marginBottom: SPACING.sm,
  },
  thumbGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: SPACING.sm,
  },
  hiddenNote: {
    ...TYPOGRAPHY.caption,
    marginTop: SPACING.sm,
  },
  cropStep: {
    marginTop: SPACING.sm,
  },
  rotateRow: {
    flexDirection: 'row',
    gap: SPACING.sm,
    marginBottom: SPACING.md,
  },
  noteInput: {
    minHeight: 72,
    textAlignVertical: 'top',
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: SPACING.lg,
  },
  scrollSentinel: {
    height: 1,
  },
  thumbCaption: {
    ...TYPOGRAPHY.caption,
    textAlign: 'center',
    paddingVertical: 4,
  },
});
